import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { fmtDateTime as fmtDateTimeTz } from "../lib/tz";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader, GhostButton, ActionButton, PillBadge, inputClass } from "../components/Primitives";
import LinkedEntityFilters from "../components/LinkedEntityFilters";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown";
import { SortableHeader, useSortableRows } from "../components/SortableTable";
import { EditableTimestampCell } from "../components/EditableTimestampCell";
import { LogAttendanceModal } from "../components/attendance/LogAttendanceModal";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { LogIn, LogOut, Filter, X, ChevronDown, ChevronRight, Download, Pencil, ClipboardCheck, Calendar as CalendarIcon } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONES = { in_progress: "amber", completed: "green", no_show: "red", scheduled: "blue", auto_closed: "red" };
const EDITOR_ROLES = new Set(["admin", "supervisor"]);
// Roles allowed to backfill missed attendance via the "Log Attendance" button.
// Mirrors the backend `_LOG_ATTENDANCE_ROLES` guard.
const LOG_ATTENDANCE_ROLES = new Set([
  "super_admin", "admin", "chief_security_officer",
  "operations_manager", "supervisor",
]);

function fmtDateTime(iso, tz) {
  if (!iso) return "—";
  try {
    return fmtDateTimeTz(iso, tz, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      ...(tz ? { timeZoneName: "short" } : {}),
    });
  } catch { return "—"; }
}

function fmtHours(h) {
  if (h === null || h === undefined) return "—";
  return `${h.toFixed(2)}h`;
}

function buildQuery(filters) {
  const params = new URLSearchParams();
  const { entity, start, end, status, deductUnpaidBreaks } = filters;
  if (entity.guard_ids.length) params.set("guard_id", entity.guard_ids.join(","));
  if (entity.site_ids.length) params.set("site_id", entity.site_ids.join(","));
  if (entity.client_names.length) params.set("client_name", entity.client_names.join(","));
  if (start) params.set("start", new Date(`${start}T00:00:00`).toISOString());
  if (end) params.set("end", new Date(`${end}T23:59:59.999`).toISOString());
  if (Array.isArray(status) && status.length) params.set("status", status.join(","));
  // Always emit so exports respect the toggle explicitly.
  params.set("deduct_unpaid_breaks", deductUnpaidBreaks ? "true" : "false");
  return params;
}

/**
 * Inline editor for per-shift break-override minutes.
 * Opens a small popover with (minutes, reason) + Save/Clear/Cancel.
 * • Empty minutes + Save = clear override on this side (revert to site default).
 * • Reason is optional but recommended and audit-logged.
 * Read-only when `!canEdit`.
 */
function BreakOverrideCell({ row, side, hoursDefault, canEdit, onSave, testId }) {
  const currentMin = side === "paid"
    ? row.break_override?.paid_minutes
    : row.break_override?.unpaid_minutes;
  const currentReason = row.break_override?.reason || "";
  const isOverride = currentMin !== null && currentMin !== undefined;
  const belowThreshold = !row.break_threshold_met;
  const displayHours = hoursDefault != null && hoursDefault > 0 ? `${hoursDefault.toFixed(2)}h` : "—";
  const [open, setOpen] = useState(false);
  const [draftMin, setDraftMin] = useState(isOverride ? String(currentMin) : "");
  const [draftReason, setDraftReason] = useState(currentReason);

  // Reset draft when opening
  useEffect(() => {
    if (open) {
      setDraftMin(isOverride ? String(currentMin) : "");
      setDraftReason(currentReason);
    }
  }, [open, isOverride, currentMin, currentReason]);

  const commit = () => {
    const raw = (draftMin ?? "").trim();
    const reason = (draftReason ?? "").trim() || null;
    if (raw === "" && !isOverride) { setOpen(false); return; }
    if (raw === "") {
      // Clear override entirely (revert to site default)
      onSave(null, reason);
      setOpen(false);
      return;
    }
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 0 || n > 1440) return;
    if (isOverride && n === currentMin && reason === (currentReason || null)) {
      setOpen(false);
      return;
    }
    onSave(n, reason);
    setOpen(false);
  };

  const label = (
    <>
      {displayHours}
      {isOverride && <span className="ml-1 text-[9px] uppercase tracking-widest">•ovr</span>}
    </>
  );

  if (!canEdit) {
    return (
      <span className="font-mono text-xs text-zinc-400" data-testid={testId}>
        {label}
      </span>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          title={belowThreshold
            ? "Shift below site threshold — overrides here don't affect the deduction until the shift meets threshold."
            : (isOverride ? `Override: ${currentMin} min${currentReason ? ` · ${currentReason}` : ""} · click to edit` : "Click to override this shift's break minutes")}
          className={`font-mono text-xs px-1.5 py-0.5 rounded border border-transparent hover:border-[#3F3F46] hover:bg-black/20 transition-colors ${isOverride ? "text-amber-300" : "text-zinc-400"}`}
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 bg-[#18181B] border-[#27272A] p-3 space-y-2.5"
        data-testid={`${testId}-popover`}
      >
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">
          {side === "paid" ? "Paid break override" : "Unpaid break override"}
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Minutes</label>
          <input
            type="number"
            min={0}
            max={1440}
            step={1}
            autoFocus
            value={draftMin}
            onChange={(e) => setDraftMin(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commit(); }
              else if (e.key === "Escape") { setOpen(false); }
            }}
            placeholder="Leave blank to clear override"
            className="w-full bg-black/40 border border-[#3F3F46] rounded px-2 py-1.5 font-mono text-sm text-zinc-100 focus:outline-none focus:border-[#FF3B30]"
            data-testid={`${testId}-input`}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Reason <span className="text-zinc-600 normal-case tracking-normal">(optional, audited)</span></label>
          <input
            type="text"
            maxLength={500}
            value={draftReason}
            onChange={(e) => setDraftReason(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commit(); }
              else if (e.key === "Escape") { setOpen(false); }
            }}
            placeholder="e.g. Worked through lunch"
            className="w-full bg-black/40 border border-[#3F3F46] rounded px-2 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-[#FF3B30]"
            data-testid={`${testId}-reason`}
          />
        </div>
        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            onClick={commit}
            className="px-3 py-1.5 rounded bg-[#FF3B30] hover:bg-[#FF453A] text-white text-xs uppercase tracking-widest font-medium"
            data-testid={`${testId}-save`}
          >
            Save
          </button>
          {isOverride && (
            <button
              type="button"
              onClick={() => { onSave(null, null); setOpen(false); }}
              className="px-3 py-1.5 rounded border border-[#3F3F46] hover:border-[#FF3B30] text-zinc-300 text-xs uppercase tracking-widest"
              data-testid={`${testId}-clear`}
            >
              Clear
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300"
          >
            Cancel
          </button>
        </div>
        {belowThreshold && (
          <div className="text-[10px] text-amber-400/80 leading-snug">
            This shift is below the site&rsquo;s break-threshold — override saved but not deducted until the shift meets threshold.
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}


const DEFAULT_FILTERS = {
  entity: { client_names: [], site_ids: [], guard_ids: [] },
  start: "",
  end: "",
  status: [], // multi-select of: in_progress | completed | no_show
  deductUnpaidBreaks: true,
};

const STATUS_OPTIONS = [
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "no_show", label: "No show" },
];

export default function Attendance() {
  const { user } = useAuth();
  const canEdit = EDITOR_ROLES.has(user?.role);
  const canLogAttendance = LOG_ATTENDANCE_ROLES.has(user?.role);
  // Deep-link hydration: read the URL synchronously on first render so
  // the initial /attendance fetch already has the right filters — avoids
  // a stale "no filter" response racing the post-hydration "with filter"
  // one. iter334 — full filter round-trip: every dimension (client / site
  // / guard / start / end / status / deductUnpaidBreaks) is mirrored to
  // the URL query so a refresh keeps the same view. This also makes the
  // page bookmarkable / shareable with any filter set applied.
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [filters, setFilters] = useState(() => {
    const csv = (k) => (searchParams.get(k) || "").split(",").filter(Boolean);
    // Back-compat with older `?site=<id>` / `?missed=1` deep-links.
    const legacySite = searchParams.get("site");
    const legacyMissed = searchParams.get("missed") === "1";
    const status = csv("status");
    return {
      entity: {
        client_names: csv("client_name"),
        site_ids: csv("site_id").length ? csv("site_id") : (legacySite ? [legacySite] : []),
        guard_ids: csv("guard_id"),
      },
      start: searchParams.get("start") || "",
      end: searchParams.get("end") || "",
      status: status.length ? status : (legacyMissed ? ["no_show"] : []),
      deductUnpaidBreaks: searchParams.get("deduct_unpaid_breaks") !== "false",
    };
  });
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [logAttendanceOpen, setLogAttendanceOpen] = useState(false);
  // Pre-fill payload set when the user clicks "Log" on a specific no-show row.
  const [logAttendancePrefill, setLogAttendancePrefill] = useState(null);

  const loadAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      const { client_names, site_ids, guard_ids } = filters.entity;
      if (guard_ids.length) params.guard_id = guard_ids.join(",");
      if (site_ids.length) params.site_id = site_ids.join(",");
      if (client_names.length) params.client_name = client_names.join(",");
      if (filters.status && filters.status.length) params.status = filters.status.join(",");
      if (filters.start) {
        // iter332 — parse as local midnight, not UTC, so filtering
        // "from 2026-02-15" doesn't include Feb 14 evening (in TZs
        // west of UTC) or Feb 15 evening (east of UTC).
        params.start = new Date(`${filters.start}T00:00:00`).toISOString();
      }
      if (filters.end) {
        // iter332 — local end-of-day, same reasoning as above. The
        // previous `new Date(YYYY-MM-DD).setHours(23,59,59,999)`
        // trick set UTC midnight first then adjusted local hours,
        // which dropped the last selected day for negative-offset TZs.
        params.end = new Date(`${filters.end}T23:59:59.999`).toISOString();
      }
      params.deduct_unpaid_breaks = filters.deductUnpaidBreaks ? "true" : "false";
      const r = await api.get("/attendance", { params });
      setRows(r.data || []);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load attendance"));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadAttendance(); }, [loadAttendance]);

  // iter334 — Mirror the current filter state to the URL so a browser
  // refresh (or a bookmarked/shared link) keeps the same view. Uses
  // `replace` so back-button history doesn't accumulate a step per
  // dropdown toggle. Only writes when something is actually filtered
  // (keeps the URL clean for the default view).
  useEffect(() => {
    const next = new URLSearchParams();
    const { client_names, site_ids, guard_ids } = filters.entity;
    if (client_names.length) next.set("client_name", client_names.join(","));
    if (site_ids.length) next.set("site_id", site_ids.join(","));
    if (guard_ids.length) next.set("guard_id", guard_ids.join(","));
    if (filters.status && filters.status.length) next.set("status", filters.status.join(","));
    if (filters.start) next.set("start", filters.start);
    if (filters.end) next.set("end", filters.end);
    if (filters.deductUnpaidBreaks === false) next.set("deduct_unpaid_breaks", "false");
    // Compare stringified forms to avoid an infinite re-render loop —
    // URLSearchParams identity always differs so we need a value check.
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [filters, searchParams, setSearchParams]);

  const getPosition = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({});
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => resolve({}),
        { timeout: 5000, enableHighAccuracy: true }
      );
    });

  const mark = async (shift_id, action) => {
    setBusy(`${shift_id}:${action}`);
    const pos = await getPosition();
    try {
      await api.post("/attendance", { shift_id, action, ...pos });
      toast.success(action === "check_in" ? "Checked in" : "Checked out");
      loadAttendance();
    } catch (e) {
      const msg = errorMessage(e, "Failed");
      toast.error(typeof msg === "string" ? msg : "Failed");
    } finally { setBusy(null); }
  };

  const saveTimestamp = async (row, field, isoValue, reason) => {
    const next = {
      check_in_time: field === "check_in_time" ? isoValue : row.check_in_time,
      check_out_time: field === "check_out_time" ? isoValue : row.check_out_time,
      edit_reason: reason || null,
    };
    try {
      const res = await api.patch(`/attendance/session/${row.shift_id}/${row.guard_id}`, next);
      if (res.data?.locked_period_edit) {
        const lp = res.data.locked_period || {};
        toast.warning(
          `${field === "check_in_time" ? "Clock-in" : "Clock-out"} updated — POST-LOCK edit (period ${lp.period_start} → ${lp.period_end}); flagged in audit log.`,
          { duration: 8000 },
        );
      } else {
        toast.success(field === "check_in_time" ? "Clock-in updated" : "Clock-out updated");
      }
      // Optimistically reflect — full reload guarantees server truth
      loadAttendance();
    } catch (e) {
      toast.error(errorMessage(e, "Update failed"));
      throw e;
    }
  };

  const saveBreakOverride = async (row, side, minutesOrNull, reason = null) => {
    // Preserve the other side of the override (server treats null as
    // "clear this side"). Bail out when the caller re-submits the exact
    // same value as what's on the server to avoid noisy audit rows.
    const currentPaid = row.break_override?.paid_minutes ?? null;
    const currentUnpaid = row.break_override?.unpaid_minutes ?? null;
    const currentReason = row.break_override?.reason || null;
    const nextPaid = side === "paid" ? minutesOrNull : currentPaid;
    const nextUnpaid = side === "unpaid" ? minutesOrNull : currentUnpaid;
    const cleanReason = (reason || "").trim() || null;
    if (nextPaid === currentPaid && nextUnpaid === currentUnpaid && cleanReason === currentReason) return;
    try {
      await api.patch(
        `/attendance/session/${row.shift_id}/${row.guard_id}/breaks`,
        { paid_minutes: nextPaid, unpaid_minutes: nextUnpaid, reason: cleanReason },
      );
      toast.success(minutesOrNull === null ? "Override cleared" : "Break override saved");
      loadAttendance();
    } catch (e) {
      toast.error(errorMessage(e, "Update failed"));
    }
  };

  const totalHours = useMemo(
    () => rows.reduce((acc, r) => acc + (r.total_hours || 0), 0),
    [rows]
  );

  const sortAccessors = useMemo(() => ({
    guard: (r) => (r.guard_name || "").toLowerCase(),
    site: (r) => (r.site_name || "").toLowerCase(),
    shift_start: (r) => r.shift_start || "",
    scheduled_hours: (r) => Number(r.scheduled_hours) || 0,
    check_in_time: (r) => r.check_in_time || "",
    check_out_time: (r) => r.check_out_time || "",
    total_hours: (r) => Number(r.total_hours) || 0,
    status: (r) => (r.status || "").toLowerCase(),
    edits: (r) => Number(r.edited_count) || 0,
  }), []);
  const sortState = useSortableRows(rows, sortAccessors, { defaultKey: "shift_start", defaultDir: "desc", storageKey: "attendance" });

  const setEntity = (next) => setFilters((f) => ({ ...f, entity: next }));
  const updateFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => setFilters(DEFAULT_FILTERS);
  const hasFilters =
    filters.entity.client_names.length ||
    filters.entity.site_ids.length ||
    filters.entity.guard_ids.length ||
    filters.start || filters.end || (filters.status && filters.status.length);

  // iter335 — Quick range presets (1w / 2w / 1mo). Sets `start` and
  // `end` to LOCAL YYYY-MM-DD boundaries so the value round-trips
  // cleanly through the URL sync (which reads `filters.start` verbatim
  // and only converts to ISO in the API call).
  const toLocalDateStr = (d) =>
    new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const applyRange = useCallback((days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    setFilters((f) => ({ ...f, start: toLocalDateStr(start), end: toLocalDateStr(end) }));
  }, []);
  const activeRange = useMemo(() => {
    if (!filters.start || !filters.end) return null;
    const today = toLocalDateStr(new Date());
    if (filters.end !== today) return "custom";
    const diffDays = Math.round(
      (new Date(`${filters.end}T00:00:00`) - new Date(`${filters.start}T00:00:00`)) / 86400000
    );
    return [7, 14, 30].includes(diffDays) ? diffDays : "custom";
  }, [filters.start, filters.end]);
  const RANGE_PRESETS = [
    { label: "1w", days: 7 },
    { label: "2w", days: 14 },
    { label: "1mo", days: 30 },
  ];

  return (
    <div>
      <PageHeader
        title="Attendance"
        subtitle="Check  in/out. Every row shows the live clock-in / clock-out timestamps and total hours worked."
        action={
          canLogAttendance ? (
            <ActionButton
              data-testid="log-attendance-button"
              onClick={() => setLogAttendanceOpen(true)}
            >
              <ClipboardCheck className="size-4" /> Log Attendance
            </ActionButton>
          ) : null
        }
      />

      <div className="p-8 space-y-6">
        <div className="border border-[#27272A] bg-[#121212]">
          <div className="px-5 py-4 border-b border-[#27272A] flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="label-mute">Operations</div>
              <div className="font-display text-xl uppercase font-bold tracking-tight">Shift Attendance</div>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2 text-xs font-mono text-zinc-400">
              <span data-testid="attendance-session-count">{rows.length} shifts</span>
              <span className="text-zinc-600">·</span>
              <span data-testid="attendance-total-hours">{totalHours.toFixed(2)}h total</span>
              <span className="text-zinc-600">·</span>
              <div className="inline-flex items-center gap-1" role="group" aria-label="Quick date range">
                {RANGE_PRESETS.map((p) => (
                  <button
                    key={p.days}
                    type="button"
                    data-testid={`attendance-range-${p.label}`}
                    onClick={() => applyRange(p.days)}
                    title={`Show the last ${p.days} days`}
                    className={`text-[10px] uppercase tracking-widest border px-2 py-1 transition-colors ${
                      activeRange === p.days
                        ? "border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/5"
                        : "border-[#27272A] text-zinc-400 hover:text-white hover:border-zinc-500"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
                {/* iter336 — Custom range: opens both From/To calendars
                    in one popover so ops can pick an arbitrary window
                    without expanding the full filter drawer. */}
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="attendance-range-custom"
                      title={
                        activeRange === "custom" && filters.start && filters.end
                          ? `Custom: ${filters.start} → ${filters.end}`
                          : "Pick a custom From/To range"
                      }
                      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border px-2 py-1 transition-colors ${
                        activeRange === "custom"
                          ? "border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/5"
                          : "border-[#27272A] text-zinc-400 hover:text-white hover:border-zinc-500"
                      }`}
                    >
                      <CalendarIcon className="size-3" />
                      Custom
                      {activeRange === "custom" && filters.start && filters.end && (
                        <span className="hidden sm:inline font-mono normal-case tracking-normal ml-0.5">
                          {filters.start.slice(5)}→{filters.end.slice(5)}
                        </span>
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="p-0 w-auto bg-[#0A0A0A] border-[#27272A]"
                    align="end"
                    data-testid="attendance-range-custom-popover"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-[#27272A]">
                      <div className="p-3">
                        <div className="label-mute mb-2 flex items-center justify-between">
                          <span>From</span>
                          {filters.start && (
                            <button
                              type="button"
                              onClick={() => updateFilter("start", "")}
                              className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30]"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                        <Calendar
                          mode="single"
                          selected={filters.start ? new Date(`${filters.start}T00:00:00`) : undefined}
                          onSelect={(d) =>
                            updateFilter("start", d ? toLocalDateStr(d) : "")
                          }
                          disabled={(d) =>
                            filters.end ? d > new Date(`${filters.end}T00:00:00`) : false
                          }
                          initialFocus
                        />
                      </div>
                      <div className="p-3">
                        <div className="label-mute mb-2 flex items-center justify-between">
                          <span>To</span>
                          {filters.end && (
                            <button
                              type="button"
                              onClick={() => updateFilter("end", "")}
                              className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30]"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                        <Calendar
                          mode="single"
                          selected={filters.end ? new Date(`${filters.end}T00:00:00`) : undefined}
                          onSelect={(d) =>
                            updateFilter("end", d ? toLocalDateStr(d) : "")
                          }
                          disabled={(d) =>
                            filters.start ? d < new Date(`${filters.start}T00:00:00`) : false
                          }
                        />
                      </div>
                    </div>
                    {(filters.start || filters.end) && (
                      <button
                        type="button"
                        data-testid="attendance-range-custom-clear"
                        onClick={() => setFilters((f) => ({ ...f, start: "", end: "" }))}
                        className="w-full text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] py-2 border-t border-[#27272A]"
                      >
                        Clear range
                      </button>
                    )}
                  </PopoverContent>
                </Popover>
              </div>
              <span className="text-zinc-600">·</span>
              <button
                type="button"
                data-testid="attendance-missed-toggle"
                onClick={() => {
                  const isMissedOnly = filters.status.length === 1 && filters.status[0] === "no_show";
                  setFilters((f) => ({ ...f, status: isMissedOnly ? [] : ["no_show"] }));
                }}
                className={`text-[10px] uppercase tracking-widest border px-2 py-1 transition-colors ${filters.status.length === 1 && filters.status[0] === "no_show" ? "border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/5" : "border-[#27272A] text-zinc-500 hover:text-[#FF3B30] hover:border-[#FF3B30]"}`}
                title="Show only shifts with no check-in — backfill candidates"
              >
                Missed only
              </button>
            </div>
          </div>

          {/* Filters (collapsible) */}
          <div className="border-b border-[#27272A] bg-[#0E0E0E]">
            <button
              type="button"
              data-testid="attendance-filters-toggle"
              onClick={() => setFiltersOpen((o) => !o)}
              className="w-full px-5 py-3 flex items-center justify-between gap-2 hover:bg-[#141414] transition-colors"
            >
              <div className="flex items-center gap-2">
                {filtersOpen ? <ChevronDown className="size-3.5 text-zinc-500" /> : <ChevronRight className="size-3.5 text-zinc-500" />}
                <Filter className="size-3.5 text-zinc-500" />
                <span className="label-mute">Filters</span>
                {hasFilters && (
                  <span className="text-[10px] uppercase tracking-widest text-[#FF3B30] ml-2" data-testid="attendance-filters-active-badge">
                    · {[
                      filters.entity.client_names.length && `${filters.entity.client_names.length} client`,
                      filters.entity.site_ids.length && `${filters.entity.site_ids.length} site`,
                      filters.entity.guard_ids.length && `${filters.entity.guard_ids.length} guard`,
                      filters.status && filters.status.length && `${filters.status.length} status`,
                      (filters.start || filters.end) && "date",
                    ].filter(Boolean).join(" · ")} active
                  </span>
                )}
              </div>
              {hasFilters && (
                <span
                  role="button"
                  tabIndex={0}
                  data-testid="attendance-clear-filters"
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#FF3B30] hover:text-[#FF453A]"
                  onClick={(e) => { e.stopPropagation(); clearFilters(); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); clearFilters(); } }}
                >
                  <X className="size-3" /> Clear
                </span>
              )}
            </button>
            {filtersOpen && (
              <div className="px-5 pb-4 pt-1" data-testid="attendance-filters-body">
                <LinkedEntityFilters
                  testIdPrefix="attendance-filter"
                  value={filters.entity}
                  onChange={setEntity}
                />
                <div className="grid md:grid-cols-3 gap-3 mt-3">
                  <div>
                    <span className="label-mute block mb-1.5">From</span>
                    {/* iter331 — Native date inputs were unresponsive on
                        some browsers (the popup wouldn't open on 2nd
                        focus). Switched to shadcn Popover+Calendar for a
                        consistent experience across the app. */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid="attendance-filter-start"
                          className={`${inputClass} inline-flex items-center justify-between gap-2 text-left w-full`}
                        >
                          <span className="font-mono text-xs">{filters.start || "Any date"}</span>
                          <CalendarIcon className="size-3.5 text-zinc-500" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-auto bg-[#0A0A0A] border-[#27272A]" align="start">
                        <Calendar
                          mode="single"
                          selected={filters.start ? new Date(`${filters.start}T00:00:00`) : undefined}
                          onSelect={(d) => updateFilter("start", d ? new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10) : "")}
                          initialFocus
                        />
                        {filters.start && (
                          <button
                            type="button"
                            onClick={() => updateFilter("start", "")}
                            className="w-full text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] py-2 border-t border-[#27272A]"
                          >
                            Clear
                          </button>
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <span className="label-mute block mb-1.5">To</span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid="attendance-filter-end"
                          className={`${inputClass} inline-flex items-center justify-between gap-2 text-left w-full`}
                        >
                          <span className="font-mono text-xs">{filters.end || "Any date"}</span>
                          <CalendarIcon className="size-3.5 text-zinc-500" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-auto bg-[#0A0A0A] border-[#27272A]" align="start">
                        <Calendar
                          mode="single"
                          selected={filters.end ? new Date(`${filters.end}T00:00:00`) : undefined}
                          onSelect={(d) => updateFilter("end", d ? new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10) : "")}
                          disabled={(d) => filters.start && d < new Date(`${filters.start}T00:00:00`)}
                          initialFocus
                        />
                        {filters.end && (
                          <button
                            type="button"
                            onClick={() => updateFilter("end", "")}
                            className="w-full text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] py-2 border-t border-[#27272A]"
                          >
                            Clear
                          </button>
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <span className="label-mute block mb-1.5">Status</span>
                    <MultiSelectDropdown
                      testId="attendance-filter-status"
                      label="All statuses"
                      options={STATUS_OPTIONS}
                      selected={filters.status}
                      onChange={(v) => updateFilter("status", v)}
                      searchable
                      showSelectAll
                      triggerClassName="w-full"
                    />
                  </div>
                </div>
                <label className="mt-3 flex items-start gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="attendance-deduct-unpaid-breaks"
                    className="mt-0.5 accent-[#FF3B30]"
                    checked={filters.deductUnpaidBreaks !== false}
                    onChange={(e) => updateFilter("deductUnpaidBreaks", e.target.checked)}
                  />
                  <span className="text-sm text-zinc-300">
                    Deduct unpaid breaks
                    <span className="block text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                      Uses each site&rsquo;s default unpaid-break minutes to reduce Total &amp; Adj hours (only for shifts that meet the site&rsquo;s threshold).
                    </span>
                  </span>
                </label>
              </div>
            )}
          </div>

          {/* Unified attendance table */}
          <div className="px-5 pt-3 pb-2 flex items-center justify-between gap-3 text-xs">
            <div className="text-zinc-500 font-mono">{rows.length} row{rows.length === 1 ? "" : "s"}</div>
            <div className="inline-flex items-center gap-2">
              <a
                data-testid="attendance-export-csv"
                href={`${process.env.REACT_APP_BACKEND_URL}/api/attendance/export?fmt=csv&${buildQuery(filters).toString()}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 border border-[#27272A] hover:border-zinc-500 px-3 py-1.5 uppercase tracking-widest text-zinc-300 hover:text-white"
              >
                <Download className="size-3" /> CSV
              </a>
              <a
                data-testid="attendance-export-xlsx"
                href={`${process.env.REACT_APP_BACKEND_URL}/api/attendance/export?fmt=xlsx&${buildQuery(filters).toString()}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 border border-[#27272A] hover:border-zinc-500 px-3 py-1.5 uppercase tracking-widest text-zinc-300 hover:text-white"
              >
                <Download className="size-3" /> Excel
              </a>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="attendance-sessions-table">
              <thead>
                <tr className="text-left border-b border-[#27272A] text-zinc-400">
                  <SortableHeader k="guard" state={sortState} className="px-5 py-3">Officer</SortableHeader>
                  <SortableHeader k="site" state={sortState} className="px-5 py-3">Site</SortableHeader>
                  <SortableHeader k="shift_start" state={sortState} className="px-5 py-3">Shift Start</SortableHeader>
                  <SortableHeader k="scheduled_hours" state={sortState} className="px-5 py-3">Sched Hrs</SortableHeader>
                  <SortableHeader k="check_in_time" state={sortState} className="px-5 py-3">Clock-In</SortableHeader>
                  <SortableHeader k="check_out_time" state={sortState} className="px-5 py-3">Clock-Out</SortableHeader>
                  <SortableHeader k="total_hours" state={sortState} className="px-5 py-3">Total Hours</SortableHeader>
                  <th className="px-5 py-3 label-mute font-normal" data-testid="attendance-col-paid-break">Paid Break</th>
                  <th className="px-5 py-3 label-mute font-normal" data-testid="attendance-col-unpaid-break">Unpaid Break</th>
                  <SortableHeader k="status" state={sortState} className="px-5 py-3">Status</SortableHeader>
                  <SortableHeader k="edits" state={sortState} className="px-5 py-3">Edits</SortableHeader>
                  <th className="px-5 py-3 label-mute font-normal">Adj In</th>
                  <th className="px-5 py-3 label-mute font-normal">Adj Out</th>
                  <th className="px-5 py-3 label-mute font-normal">Adj Hours</th>
                  <th className="px-5 py-3 label-mute font-normal">SOP Ack</th>
                  <th className="px-5 py-3 label-mute font-normal text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272A]">
                {sortState.sorted.map((r) => {
                  const isOverdue = r.auto_closed || r.status === "auto_closed";
                  const edited = (r.edited_count || 0) > 0;
                  const rowClass = isOverdue
                    ? "hover:bg-[#1c1010] bg-[#1a0a0a]/40"
                    : "hover:bg-[#161616]";
                  return (
                  <tr key={r.id} data-testid={`attendance-row-${r.id}`} className={rowClass}>
                    <td className="px-5 py-3">
                      <div className="font-medium">{r.guard_name}</div>
                      {r.badge_no && <div className="text-zinc-500 text-xs font-mono">#{r.badge_no}</div>}
                    </td>
                    <td className="px-5 py-3">
                      <div>{r.site_name}</div>
                      {r.client_name && <div className="text-zinc-500 text-xs">{r.client_name}</div>}
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-zinc-400">{fmtDateTime(r.shift_start, r.site_timezone)}</td>
                    <td className="px-5 py-3 font-mono text-xs text-zinc-400" data-testid={`attendance-scheduled-hours-${r.shift_id}`}>
                      {r.scheduled_hours != null ? `${r.scheduled_hours.toFixed(2)}h` : "—"}
                    </td>
                    <td className={`px-5 py-3 ${edited ? "bg-[#3a2a08]/40" : ""}`} data-testid={edited ? `attendance-edited-checkin-${r.shift_id}` : undefined}>
                      <div className="flex items-center gap-1.5">
                        <EditableTimestampCell
                          testId={`attendance-checkin-${r.shift_id}`}
                          value={r.check_in_time}
                          canEdit={canEdit}
                          tz={r.site_timezone}
                          currentReason={r.last_edit_reason}
                          onSave={(iso, reason) => saveTimestamp(r, "check_in_time", iso, reason)}
                        />
                        {r.auto_check_in && (
                          <span
                            className="text-[9px] uppercase tracking-widest text-[#FF9500] border border-[#FF9500]/50 px-1 py-0.5 leading-none"
                            data-testid={`attendance-checkin-auto-${r.shift_id}`}
                            title="Clock-in written by the auto-start sweeper — edit the row to override with a real clock-in time."
                          >
                            auto
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={`px-5 py-3 ${edited ? "bg-[#3a2a08]/40" : ""}`}>
                      <div className="flex items-center gap-1.5">
                        <EditableTimestampCell
                          testId={`attendance-checkout-${r.shift_id}`}
                          value={r.check_out_time}
                          canEdit={canEdit}
                          tz={r.site_timezone}
                          currentReason={r.last_edit_reason}
                          onSave={(iso, reason) => saveTimestamp(r, "check_out_time", iso, reason)}
                        />
                        {r.auto_check_out && (
                          <span
                            className="text-[9px] uppercase tracking-widest text-[#FF9500] border border-[#FF9500]/50 px-1 py-0.5 leading-none"
                            data-testid={`attendance-checkout-auto-${r.shift_id}`}
                            title="Clock-out written by the auto-start sweeper at the scheduled end time — edit the row to override with a real clock-out time."
                          >
                            auto
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs" data-testid={`attendance-hours-${r.shift_id}`}>{fmtHours(r.total_hours)}</td>
                    <td className="px-5 py-3">
                      <BreakOverrideCell
                        row={r}
                        side="paid"
                        hoursDefault={r.paid_break_hours_default}
                        canEdit={canEdit}
                        onSave={(min, reason) => saveBreakOverride(r, "paid", min, reason)}
                        testId={`attendance-paid-break-${r.shift_id}`}
                      />
                    </td>
                    <td className="px-5 py-3">
                      <BreakOverrideCell
                        row={r}
                        side="unpaid"
                        hoursDefault={r.unpaid_break_hours_default}
                        canEdit={canEdit}
                        onSave={(min, reason) => saveBreakOverride(r, "unpaid", min, reason)}
                        testId={`attendance-unpaid-break-${r.shift_id}`}
                      />
                    </td>
                    <td className="px-5 py-3">
                      <PillBadge tone={STATUS_TONES[r.status] || "default"}>
                        {(r.status || "").replace("_", " ")}
                      </PillBadge>
                      {isOverdue && (
                        <div className="text-[10px] uppercase tracking-widest text-[#FF3B30] mt-0.5" data-testid={`attendance-overdue-${r.shift_id}`}>
                          ⚠ &gt; 24h · auto-closed
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3" data-testid={`attendance-edits-${r.shift_id}`}>
                      {edited ? (
                        <div
                          className="inline-flex flex-col gap-0.5 max-w-[220px]"
                          title={
                            (r.last_edit_reason ? `Reason: ${r.last_edit_reason}\n` : "Reason: (not provided)\n") +
                            (r.last_edited_by ? `By: ${r.last_edited_by}` : "")
                          }
                        >
                          <div className="inline-flex items-center gap-1 text-[#FF9F0A]">
                            <Pencil className="size-3" />
                            <span className="font-mono text-xs">×{r.edited_count}</span>
                          </div>
                          {r.last_edit_reason && (
                            <div
                              className="text-[10px] text-zinc-400 italic truncate"
                              data-testid={`attendance-edit-reason-${r.shift_id}`}
                            >
                              {r.last_edit_reason}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-zinc-600 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-zinc-400" data-testid={`attendance-adj-in-${r.shift_id}`}>
                      {r.adj_in_time ? fmtDateTime(r.adj_in_time, r.site_timezone) : "—"}
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-zinc-400" data-testid={`attendance-adj-out-${r.shift_id}`}>
                      {r.adj_out_time ? fmtDateTime(r.adj_out_time, r.site_timezone) : "—"}
                    </td>
                    <td className="px-5 py-3 font-mono text-xs" data-testid={`attendance-adj-hours-${r.shift_id}`}>
                      {r.adj_hours != null ? `${r.adj_hours.toFixed(2)}h` : "—"}
                    </td>
                    <td className="px-5 py-3" data-testid={`attendance-sop-ack-${r.shift_id}`}>
                      {r.sop_acknowledged_at ? (
                        <div className="inline-flex items-center gap-1 text-[#34C759]" title={`SOP acknowledged at ${fmtDateTime(r.sop_acknowledged_at, r.site_timezone)}`}>
                          <span className="text-sm">✓</span>
                          <span className="text-[10px] uppercase tracking-widest">SOP</span>
                        </div>
                      ) : (
                        <span className="text-zinc-600 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex gap-1 justify-end">
                        {canLogAttendance && !r.check_in_time && (r.status === "no_show" || r.status === "auto_closed") && (
                          <GhostButton
                            data-testid={`log-attendance-row-${r.shift_id}-${r.guard_id}`}
                            onClick={() => setLogAttendancePrefill({
                              shift_id: r.shift_id,
                              guard_id: r.guard_id,
                              site_id: r.site_id,
                              check_in: r.shift_start,
                            })}
                            title="Backfill attendance for this missed shift"
                          >
                            <ClipboardCheck className="size-3.5" />Log
                          </GhostButton>
                        )}
                        <GhostButton
                          data-testid={`checkin-${r.shift_id}`}
                          disabled={busy === `${r.shift_id}:check_in` || !!r.check_in_time}
                          onClick={() => mark(r.shift_id, "check_in")}
                        >
                          <LogIn className="size-3.5" />In
                        </GhostButton>
                        <GhostButton
                          data-testid={`checkout-${r.shift_id}`}
                          disabled={busy === `${r.shift_id}:check_out` || !r.check_in_time || !!r.check_out_time}
                          onClick={() => mark(r.shift_id, "check_out")}
                        >
                          <LogOut className="size-3.5" />Out
                        </GhostButton>
                      </div>
                    </td>
                  </tr>
                );})}
                {rows.length === 0 && !loading && (
                  <tr><td colSpan={16} className="px-5 py-12 text-center text-zinc-500 scanlines">No shifts match the filters.</td></tr>
                )}
                {loading && (
                  <tr><td colSpan={16} className="px-5 py-8 text-center text-zinc-500 text-xs font-mono">Loading…</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {(logAttendanceOpen || logAttendancePrefill) && (
        <LogAttendanceModal
          onClose={() => { setLogAttendanceOpen(false); setLogAttendancePrefill(null); }}
          onSaved={loadAttendance}
          prefill={logAttendancePrefill}
        />
      )}
    </div>
  );
}
